import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { api } from '../lib/api';
import './RecategorizationPanel.css';

const RecategorizationPanel = ({ onRecategorizeComplete }) => {
  const [isRecategorizing, setIsRecategorizing] = useState(false);
  const [result, setResult] = useState(null);
  const [forceRecategorize, setForceRecategorize] = useState(false);
  const [limit, setLimit] = useState(100);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const handleRecategorize = async () => {
    setIsRecategorizing(true);
    setResult(null);

    try {
      const data = await api.recategorizeTransactions({
        forceRecategorize,
        startDate: dateRange.startDate || null,
        endDate: dateRange.endDate || null,
        limit
      });

      setResult({
        success: true,
        message: data.message,
        updated: data.updated || 0
      });

      // Notify parent component to refresh data
      if (onRecategorizeComplete) {
        onRecategorizeComplete(data);
      }
    } catch (error) {
      console.error('Re-categorization error:', error);

      if (error.name === 'AbortError') {
        setResult({
          success: false,
          message: 'Re-categorization timed out after 10 minutes. Try reducing the batch size.'
        });
      } else {
        setResult({
          success: false,
          message: 'Network error during re-categorization'
        });
      }
    } finally {
      setIsRecategorizing(false);
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="recategorization-panel">
      <div className="panel-header">
        <h3>🤖 Auto Re-categorization</h3>
        <p className="panel-description">
          Re-run categorization on existing transactions using the latest keywords and improved matching logic.
        </p>
      </div>

      <div className="panel-content">
        {/* Date Range Filter */}
        <div className="date-range-section">
          <h4>Date Range (Optional)</h4>
          <div className="date-inputs">
            <div className="date-field">
              <label htmlFor="recategorize-start-date">Start Date:</label>
              <input
                id="recategorize-start-date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                disabled={isRecategorizing}
              />
            </div>
            <div className="date-field">
              <label htmlFor="recategorize-end-date">End Date:</label>
              <input
                id="recategorize-end-date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                disabled={isRecategorizing}
              />
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="options-section">
          <div className="option-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={forceRecategorize}
                onChange={(e) => setForceRecategorize(e.target.checked)}
                disabled={isRecategorizing}
              />
              <span className="checkmark"></span>
              Force re-categorize ALL transactions (including already categorized ones)
            </label>
            <div className="option-help">
              {forceRecategorize ? (
                <span className="warning">⚠️ This will re-categorize ALL transactions, potentially overriding manual categories</span>
              ) : (
                <span className="info">ℹ️ Re-categorize Uncategorized transactions, and fill in a person when a keyword has a default and the charge does not have one yet</span>
              )}
            </div>
          </div>

          <div className="option-row">
            <label htmlFor="limit-input" className="limit-label">
              Batch size (max transactions to process):
            </label>
            <input
              id="limit-input"
              type="number"
              min="10"
              max="500"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
              disabled={isRecategorizing}
              className="limit-input"
            />
            <div className="option-help">
              <span className="info">ℹ️ Smaller batches are faster and less likely to timeout. Recommended: 50-100</span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="action-section">
          <button
            className={`recategorize-btn ${isRecategorizing ? 'loading' : ''}`}
            onClick={handleRecategorize}
            disabled={isRecategorizing}
          >
            {isRecategorizing ? (
              <>
                <span className="spinner"></span>
                Re-categorizing...
              </>
            ) : (
              <>
                <span className="icon">🔄</span>
                Start Re-categorization
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div className={`result-section ${result.success ? 'success' : 'error'}`}>
            <div className="result-icon">
              {result.success ? '✅' : '❌'}
            </div>
            <div className="result-content">
              <div className="result-message">{result.message}</div>
              {result.success && result.updated > 0 && (
                <div className="result-details">
                  {result.updated} transaction{result.updated !== 1 ? 's' : ''} were re-categorized
                </div>
              )}
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="help-section">
          <h4>How it works:</h4>
          <ul>
            <li>✨ <strong>Improved Matching:</strong> Removes store numbers (like #1360) for better keyword matching</li>
            <li>🎯 <strong>Fuzzy Matching:</strong> Uses similarity algorithms to catch variations</li>
            <li>🔄 <strong>Versioned Updates:</strong> Creates new transaction versions preserving history</li>
            <li>📅 <strong>Date Filtering:</strong> Optionally limit re-categorization to specific date ranges</li>
          </ul>

          <div className="examples">
            <h5>Examples of improved matching:</h5>
            <ul>
              <li><code>costco whse#1360</code> → <code>costco whse</code> → <strong>Groceries</strong></li>
              <li><code>amazon.com*5f60r78n3</code> → <code>amazon</code> → <strong>Amazon</strong></li>
              <li><code>trader joe #435</code> → <code>trader joe</code> → <strong>Groceries</strong></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

RecategorizationPanel.propTypes = {
  onRecategorizeComplete: PropTypes.func
};

export default RecategorizationPanel;
